import {connect} from 'react-redux';

import {PanelTypesEnum, ViewerPanelId} from '../../../libs/shared/core/src/store/panels/panelStateBase';
import {
  createSelector,
  makeMapDispatchToProps,
  makeMapStateToProps,
} from '../../../srcCommon/helpers/reselectHelpers';
import {DetailedActivityModel, isLinkedConversationActivity} from '../../models/api/activityModel';
import {AppState} from '../../store/appState';
import {ThunkAction} from '../../store/appThunk';
import {deleteAnnotationAction} from '../../store/data/annotations/annotationsActions';
import {unlinkConversationAsyncAction} from '../../store/data/conversations/linkedConversations/linkedConversationsActions';
import {getActivities, getComments, getFlatActivities} from '../../store/data/dataSelectors';
import {getPanels} from '../../store/panels/panelsSelectors';
import {forceInternalComposerPopoutAction} from '../../store/panels/viewer/viewerPanelActions';
import {ViewerPanelPagesState, ViewerPanelPageTypesEnum} from '../../store/panels/viewer/viewerPanelState';
import {PopupViewer} from './popupViewer';

/*
 * Props.
 */

interface PopupViewerContainerProps {
  panelId: ViewerPanelId;
  pages: ViewerPanelPagesState;
  composeCommentUid: string | undefined;
}

/*
 * Selectors.
 */

function getPages(_state: AppState, props: PopupViewerContainerProps) {
  return props.pages;
}

function getComposeCommentUid(_state: AppState, props: PopupViewerContainerProps) {
  return props.composeCommentUid;
}

function makeGetComposeComment() {
  return createSelector(
    [getComments, getComposeCommentUid],
    (comments, composeCommentUid) => (composeCommentUid && comments[composeCommentUid]) || undefined,
  );
}

function makeGetIsComposing(getComment: ReturnType<typeof makeGetComposeComment>) {
  return createSelector([getComment, getPages], (comment, pages) =>
    Boolean(comment && pages.current.type === ViewerPanelPageTypesEnum.NO_CONVERSATION),
  );
}

function makeGetShouldConfirmClose(
  getComment: ReturnType<typeof makeGetComposeComment>,
  getIsComposing: ReturnType<typeof makeGetIsComposing>,
) {
  return createSelector([getIsComposing, getComment], (isComposing, comment) => {
    if (!isComposing || !comment) {
      return false;
    }

    const hasTeammates = Boolean(comment.teammateIds && comment.teammateIds.length > 0);
    const hasAttachments = comment.attachments.length > 0;
    const hasTopic = Boolean(comment.topic);
    const hasText = Boolean(comment.text);
    const isNewLinkedDiscussion = Boolean(comment.conversationId);

    return hasTeammates || hasAttachments || hasTopic || hasText || isNewLinkedDiscussion;
  });
}

function getPanelId(_state: AppState, props: PopupViewerContainerProps) {
  return props.panelId;
}

function makeGetPanel() {
  return createSelector([getPanels, getPanelId], (panels, panelId) => {
    const panel = panels[panelId];
    if (panel?.type === PanelTypesEnum.VIEWER) {
      return panel;
    }

    return undefined;
  });
}

function makeGetOriginalLinkedDiscussionActivity() {
  return createSelector([makeGetPanel(), getActivities], (panel, activities) => {
    if (!panel) {
      return null;
    }
    const {linkedActivityId} = panel;
    if (linkedActivityId === undefined) {
      return null;
    }
    const activity = activities[linkedActivityId];
    return isLinkedConversationActivity(activity) ? activity : null;
  });
}

/*
 * Actions.
 */

function onConfirmDeleteAction(linkedConversationActivity: DetailedActivityModel): ThunkAction {
  return (dispatch, getState) => {
    dispatch(unlinkConversationAsyncAction(linkedConversationActivity));

    if (!linkedConversationActivity.annotationUid) {
      // If there's no annotation, we're done.
      return;
    }

    // If there is an annotation, we need to remove it from the original conversation.
    // Otherwise, it will remain in the store until GC.
    const findAnnotationUidInOriginalConversation = () => {
      const activities = getFlatActivities(getState());

      for (const activity of activities) {
        if (
          isLinkedConversationActivity(activity) &&
          activity.linkedConversationId === linkedConversationActivity.conversationId
        ) {
          // We expect `annotationUid` should exist
          return activity.annotationUid;
        }
      }

      return undefined;
    };

    const annotationUid = findAnnotationUidInOriginalConversation();
    if (annotationUid) {
      dispatch(deleteAnnotationAction(annotationUid));
    }
  };
}

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<PopupViewerContainerProps>()(() => {
  const getComposeComment = makeGetComposeComment();
  const getIsComposing = makeGetIsComposing(getComposeComment);
  return {
    composeComment: getComposeComment,
    isComposing: getIsComposing,
    shouldConfirmClose: makeGetShouldConfirmClose(getComposeComment, getIsComposing),
    originalLinkedConversationActivity: makeGetOriginalLinkedDiscussionActivity(),
  };
});

const mapDispatchToProps = makeMapDispatchToProps<PopupViewerContainerProps>()(({panelId}) => ({
  onPopoutClick: () => forceInternalComposerPopoutAction(panelId),
  onConfirmDeleteClick: onConfirmDeleteAction,
}));

export const PopupViewerContainer = connect(mapStateToProps, mapDispatchToProps)(PopupViewer);
